import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles, Box, TableCell, TableRow } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import _ from 'lodash';

import { PlusIcon, VisibleIcon } from 'assets/svg';
import Routes from 'router/Routes';
import { formatDate } from 'utils/formatters';
import Progress from 'components/Main/Progress';
import LoadError from 'components/Main/LoadError';
import PaginatedTable from 'components/PaginatedTable';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import IconButton, { DeleteIconButton, EditIconButton } from 'components/Buttons/IconButtons';
import useCheckPermissions from 'utils/useCheckPermissions';
import { handledErrorMessage } from 'utils/apiError';

import FilterRow from './FilterRow';
import API from 'apis/API';

const useStyles = makeStyles(() => ({
	table: {
		paddingInline: 0,
	}
}));

const Applications = () => {
	const classes = useStyles();
	const { t } = useTranslation(null, { keyPrefix: 'Zarządzanie WIB - Wniosek' });
	const canView = useCheckPermissions('can_view_finances', true);
	const canManage = useCheckPermissions('can_manage_finances', true);

	const columns = [
		{ title: t('Wniosek z dnia'), name: 'application_date', width: 300 },
		{ title: t('Numer wniosku'), name: 'application_number' },
		{ title: t('Status'), name: 'status' },
		{ title: t('Suma kontrolna'), name: 'controlsum' },
		{
			title: t('Akcje'),
			name: 'action',
			width: canManage ? 100 : 50,
			disableSort: true
		},
	];
	const [loading, setLoading] = useState(false);
	const [params, setParams] = useState({
		page: 1,
		column: 'application_date',
		direction: 'desc',
		filters: {}
	});
	const [totalPagesCount, setTotalPagesCount] = useState(1);
	const [data, setData] = useState(null);
	const [loadError, setLoadError] = useState(null);

	useEffect(() => loadFilteredData(), [params]);

	const handleChangeParams = _params => {
		const picked = _.pick(params, ['column', 'direction', 'page']);
		if (_.isEqual(picked, _params)) return;

		setParams({ ...params, ..._params });
	};

	const handleChangeFilters = filters => {
		setParams(prev => {
			if (_.isEqual(prev.filters, _.pickBy(filters))) return prev;

			return { ...prev, filters };
		});
	};

	const loadFilteredData = () => {
		if (loading) return;
		setLoading(true);
		setLoadError(null);
		API.management.applications.index(params).then(res => {
			setData(res.data?.data);
			setTotalPagesCount(res.data?.last_page);
		}).catch(err => setLoadError(handledErrorMessage(err))).finally(() => setLoading(false));
	};

	const handleDelete = id => () => {
		API.management.applications.delete(id).then(res => {
			if (res.data?.code === 200) {
				loadFilteredData();
				return toast.success(t('Aplikacja została usunięta!'));
			}

			toast.error(t('Nie znaleziono aplikacji'));
		});
	};

	const renderRows = () => (
		data.map((row, index) => (
			<TableRow key={index}>
				<TableCell>{formatDate(row.application_date)}</TableCell>
				<TableCell>{row.application_number}</TableCell>
				<TableCell>{row.status === 'published' ? t('Zapisany') : t('Szkic')}</TableCell>
				<TableCell>{row.controlsum}</TableCell>
				<TableCell>
					<Box display="flex">
						{canView &&
							<Link to={Routes.Management.Applications.Edit(row.id, true)}>
								<IconButton tooltip={t('Podgląd')}>
									<VisibleIcon alt="Ikona podglądu wiersza" />
								</IconButton>
							</Link>
						}
						{canManage &&
							<>
								<Link to={Routes.Management.Applications.Edit(row.id)}>
									<EditIconButton tooltip={t('Edytuj informacje')} />
								</Link>
								{(row?.status === 'draft') &&
									<DeleteIconButton tooltip={t('Usuń')} onClick={handleDelete(row.id)} />
								}
							</>
						}
					</Box>
				</TableCell>
			</TableRow>
		))
	);

	return (
		<>
			{canManage &&
				<PrimaryButton
					component={Link}
					to={Routes.Management.Applications.Create}
				>
					<PlusIcon style={{ marginRight: 10 }} />
					{t('Dodaj wniosek')}
				</PrimaryButton>
			}
			{!data
				? (loadError ? <LoadError message={loadError} onRetry={loadFilteredData} /> : <Progress status={true} />)
				: <PaginatedTable
					containerClassName={classes.table}
					columns={columns}
					exportEndpoint={API.management.applications.exportList}
					filename="Management Applications"
					totalPagesCount={totalPagesCount}
					onChangeFilters={handleChangeParams}
					defaultFilters={_.pick(params, ['column', 'direction', 'page'])}
					renderRows={renderRows}
					filterRow={<FilterRow onChange={handleChangeFilters} />}
				/>}
		</>
	);
};

export default Applications;
